/**
 * TeamRow Component
 * Display team in horizontal row format with member thumbnails and hover actions
 */

import { useState, useEffect, useCallback } from 'react';
import { hasAdminAccess } from '../../lib/roles';
import { Link } from 'react-router-dom';
import type { Team } from '../../services/teams.service';
import type { Member } from '../../types';
import { membersService } from '../../services/members.service';
import { teamsService } from '../../services/teams.service';
import { useAuth } from '../../hooks/useAuth';
import { getInitials } from '../../lib/utils';

export interface TeamRowProps {
  team: Team;
  members: Member[];
  onEdit?: (teamId: number) => void;
  onDelete?: (teamId: number) => void;
  refreshTrigger?: number;
}

export const TeamRow = ({ team, members, onEdit, onDelete, refreshTrigger }: TeamRowProps) => {
  const { user } = useAuth();
  const [loadedImages, setLoadedImages] = useState<Record<number, string>>({});
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());
  const [teamLogoUrl, setTeamLogoUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(false);

  // Handle image load error - clear cache and retry once
  const handleImageError = useCallback((memberId: number) => {
    // If already failed, don't retry again
    if (failedImages.has(memberId)) {
      // Remove from loaded images to show fallback
      setLoadedImages(prev => {
        const next = { ...prev };
        delete next[memberId];
        return next;
      });
      return;
    }

    // Mark as failed, clear cache, and retry
    setFailedImages(prev => new Set(prev).add(memberId));
    membersService.revokeMemberImage(memberId);

    // Retry loading
    membersService.fetchMemberImage(memberId).then(blobUrl => {
      if (blobUrl) {
        setLoadedImages(prev => ({ ...prev, [memberId]: blobUrl }));
      }
    });
  }, [failedImages]);

  // Defensive check: ensure members is an array
  if (!Array.isArray(members)) {
    console.error('TeamRow: members prop is not an array:', members);
    return null;
  }

  const teamMembers = members.filter((m) => m.team_id == team.id);

  // Highest-ranked member rendered on the row card.
  // Priority: longest-tenure team manager → longest-tenure team lead.
  // The backend already sorts both arrays by tenure (oldest first).
  const managers = team.managers ?? [];
  const leads = team.leads ?? [];
  const totalLeadership = managers.length + leads.length;
  const headerHighlight = managers.length > 0
    ? { label: 'Team Manager', person: managers[0] }
    : leads.length > 0
      ? { label: 'Lead', person: leads[0] }
      : null;
  const extraCount = totalLeadership > 1 ? totalLeadership - 1 : 0;

  // Load team logo as blob with JWT authentication
  useEffect(() => {
    let mounted = true;

    // Reset logo URL when refresh trigger changes to force re-fetch
    setTeamLogoUrl(null);

    const loadTeamLogo = async () => {
      if (team.has_team_logo) {
        setLogoLoading(true);
        try {
          const blobUrl = await teamsService.fetchTeamImage(team.id);
          if (blobUrl && mounted) {
            setTeamLogoUrl(blobUrl);
          }
        } catch (error) {
          console.error(`Failed to load logo for team ${team.id}:`, error);
        } finally {
          if (mounted) {
            setLogoLoading(false);
          }
        }
      }
    };

    loadTeamLogo();

    return () => {
      mounted = false;
    };
  }, [team.id, team.has_team_logo, refreshTrigger]);

  // Load member images as blobs with JWT authentication
  useEffect(() => {
    let mounted = true;

    const loadImages = async () => {
      const membersWithPictures = teamMembers.filter((m) => m.has_profile_picture);
      const imageMap: Record<number, string> = {};

      for (const member of membersWithPictures.slice(0, 12)) {
        try {
          const blobUrl = await membersService.fetchMemberImage(member.id);
          if (blobUrl && mounted) {
            imageMap[member.id] = blobUrl;
          }
        } catch (error) {
          console.error(`Failed to load image for member ${member.id}:`, error);
        }
      }

      if (mounted) {
        setLoadedImages(imageMap);
      }
    };

    if (teamMembers.length > 0) {
      loadImages();
    }

    // Cleanup: just cancel loading if component unmounts
    return () => {
      mounted = false;
    };
  }, [members, team.id]);

  return (
    <div className="group relative px-6 py-4 hover:bg-surface-hover active:bg-surface-hover transition-colors" data-testid="team-row">
      {/* Full-row tap target on touch devices (no hover to reveal actions) */}
      <Link
        to={`/teams/${team.id}`}
        className="absolute inset-0 z-10 can-hover:hidden"
        tabIndex={-1}
        aria-label={`View ${team.name}`}
      />

      <div className="flex items-center justify-between">
        {/* Team Info Section */}
        <div className="flex items-center space-x-5 flex-1 min-w-0">
          {/* Team Logo */}
          <Link to={`/teams/${team.id}`} className="flex-shrink-0 relative z-20">
            {teamLogoUrl ? (
              <img
                src={teamLogoUrl}
                alt={team.name}
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : logoLoading ? (
              <div className="w-16 h-16 rounded-full bg-surface-secondary flex items-center justify-center">
                <svg className="animate-spin h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center">
                <span className="text-xl font-bold text-white">
                  {team.name.slice(0, 2).toUpperCase()}
                </span>
              </div>
            )}
          </Link>

          {/* Team Name & Info */}
          <div className="flex-1 min-w-0">
            {/* Team Name + Department Badge */}
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-medium text-heading truncate">
                <Link
                  to={`/teams/${team.id}`}
                  className="relative z-20 hover:underline hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  {team.name}
                </Link>
              </h3>
              {team.department_name && (
                <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-status-accent text-status-accent flex-shrink-0">
                  {team.department_name}
                </span>
              )}
            </div>

            {/* Lead & Member Count */}
            <div className="mt-1 text-sm text-meta">
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0">
                {headerHighlight && (
                  <span className="flex items-center">
                    <svg className="h-4 w-4 text-blue-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {`${headerHighlight.label}: ${headerHighlight.person.first_name} ${headerHighlight.person.last_name}`}
                    {extraCount > 0 && <>{' '}<span>+{extraCount}</span></>}
                  </span>
                )}
                <span className="flex items-center">
                  <svg className="h-4 w-4 text-green-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  {teamMembers.length} member{teamMembers.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Member Thumbnails Section */}
        <div className="hidden lg:flex items-center space-x-2 px-4">
          <div className="flex -space-x-2 overflow-hidden">
            {teamMembers.slice(0, 10).map((member) => (
              <div
                key={member.id}
                className="inline-block h-9 w-9 rounded-full ring-2 ring-surface"
                title={`${member.first_name} ${member.last_name}`}
              >
                {loadedImages[member.id] ? (
                  <img
                    src={loadedImages[member.id]}
                    alt={`${member.first_name} ${member.last_name}`}
                    className="h-9 w-9 rounded-full object-cover"
                    onError={() => handleImageError(member.id)}
                  />
                ) : member.has_profile_picture && !failedImages.has(member.id) ? (
                  <div className="h-9 w-9 rounded-full bg-surface-secondary flex items-center justify-center">
                    <svg className="animate-spin h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  </div>
                ) : (
                  <div className="h-9 w-9 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center">
                    <span className="text-sm font-medium text-body">
                      {getInitials(member.first_name, member.last_name)}
                    </span>
                  </div>
                )}
              </div>
            ))}
            {teamMembers.length > 10 && (
              <div className="inline-block h-9 w-9 rounded-full ring-2 ring-surface bg-surface-secondary flex items-center justify-center">
                <span className="text-sm font-medium text-body">+{teamMembers.length - 10}</span>
              </div>
            )}
            {teamMembers.length === 0 && (
              <div className="text-sm text-meta italic">No members</div>
            )}
          </div>
        </div>

        {/* Action Buttons Section (Hidden by default, shown on hover) */}
        {hasAdminAccess(user?.role) && (
          <div className="team-actions-container hidden can-hover:block">
            <div className="team-actions flex items-center space-x-2 opacity-0 translate-x-5 pointer-events-none transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:pointer-events-auto">
              {onEdit && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onEdit(team.id);
                  }}
                  className="action-btn inline-flex items-center px-3 py-1.5 text-sm font-medium border border-blue-600 rounded-md bg-surface text-heading hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                  title="Edit Team"
                  aria-label="Edit team"
                  data-testid="edit-team-button"
                >
                  <svg className="h-4 w-4 mr-1.5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span className="hidden sm:inline">Edit</span>
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDelete(team.id);
                  }}
                  className="action-btn inline-flex items-center px-3 py-1.5 text-sm font-medium border border-blue-600 rounded-md bg-surface text-heading hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                  title="Delete Team"
                >
                  <svg className="h-4 w-4 mr-1.5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span className="hidden sm:inline">Delete</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Tap affordance — touch devices only (whole row is the tap target there) */}
        <div className="hidden touch:flex items-center text-meta flex-shrink-0 pl-1" aria-hidden="true">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </div>
      </div>
    </div>
  );
};
